import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions';
import { generateCategoryCode } from '../../shared/categoryCode';
import type { CategoriesNewLookups, CategoryFormValues, SubCategoryFormValue } from '../types/categoriesNew.types';

async function getGroupId(typeCode: string, companyId: string | null): Promise<string | null> {
  let query = supabase.from('category_groups').select('id').eq('code', typeCode);
  if (companyId) {
    query = query.or(`company_id.eq.${companyId},company_id.is.null`);
  } else {
    query = query.is('company_id', null);
  }
  const { data } = await query.maybeSingle();
  return data?.id || null;
}

function fallbackCode(name: string): string {
  return generateCategoryCode(name);
}

async function createChildren(parentId: string, groupId: string, subCategories: SubCategoryFormValue[], companyId: string) {
  for (const [index, sub] of subCategories.entries()) {
    if (!sub.name.trim()) continue;

    const { data: subData, error: subError } = await supabase
      .from('categories')
      .insert({
        company_id: companyId,
        code: sub.code.trim() || fallbackCode(sub.name),
        name: sub.name.trim(),
        description: sub.description?.trim() || '',
        group_id: groupId,
        icon_config: { name: 'ri-folder-line', tone: 'bg-gray-100 text-gray-600' },
        is_active: true,
        parent_id: parentId,
        display_order: index + 1,
      })
      .select('id')
      .single();

    if (subError) throw subError;

    const subSubCategories = sub.subSubCategories || [];
    for (const [grandIndex, grand] of subSubCategories.entries()) {
      if (!grand.name.trim()) continue;
      const { error } = await supabase.from('categories').insert({
        company_id: companyId,
        code: grand.code.trim() || fallbackCode(grand.name),
        name: grand.name.trim(),
        description: grand.description?.trim() || '',
        group_id: groupId,
        icon_config: { name: 'ri-folder-line', tone: 'bg-gray-100 text-gray-600' },
        is_active: true,
        parent_id: subData.id,
        display_order: grandIndex + 1,
      });
      if (error) throw error;
    }
  }
}

export const categoriesNewService = {
  async getLookups(): Promise<CategoriesNewLookups> {
    const { data, error } = await supabase
      .from('categories')
      .select('id,name,code,parent_id,is_active')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) return { parentOptions: [] };

    return {
      parentOptions: (data || [])
        .filter((row) => !row.parent_id)
        .map((row) => ({
          id: String(row.id),
          name: String(row.name || ''),
          code: String(row.code || ''),
        })),
    };
  },

  async createCategory(values: CategoryFormValues, userId: string, companyId: string): Promise<void> {
    // 1. Assert permission with context
    await permissionEngine.assert(userId, 'settings.categories.manage', { companyId });
    
    const groupId = await getGroupId(values.type, companyId);

    const payload: Record<string, unknown> = {
      company_id: companyId,
      code: values.code.trim() || generateCategoryCode(values.name),
      name: values.name.trim(),
      description: values.description?.trim() || '',
      icon_config: {
        name: values.icon?.trim() || 'ri-folder-line',
        tone: 'bg-emerald-100 text-emerald-700', // Default tone for new
      },
      group_id: groupId,
      is_active: values.status === 'active',
      display_order: 999,
      parent_id: values.parentId || null,
    };

    const { data: parentData, error: parentError } = await supabase
      .from('categories')
      .insert(payload)
      .select('id')
      .single();

    if (parentError) throw parentError;

    if (values.parentId) return;
    if (groupId) {
      await createChildren(String(parentData.id), groupId, values.subCategories || [], companyId);
    }
  },
};
